import React, { useEffect, useState } from "react";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import LinkIcon from "@mui/icons-material/Link";
import "./adminPictureUpload.css";
import { useMutation, useLazyQuery } from "@apollo/client";
import { NEW_CLIENT_PHOTO } from "../../../../utils/GraphQL/mutations";
import { QUERY_CLIENT_PHOTOS } from "../../../../utils/GraphQL/queries";
import { baImageUrl } from "../../../../utils/baImageUrl";
import BdrSpinner from "../../../../components/BdrSpinner/BdrSpinner";
import BdrInput from "../../../../components/BdrInput/BdrInput";


import resizeImage from "../../../../utils/formatters/resizeImage";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";

const PictureUploadAdmin = ({
  imgDate,
  changeLoading,
  changePhotoIds,
  BoaProp,
  clearState,
  clearStateTriggerCB,
  changeDateCompleted
}) => {
  const [files, setFiles] = useState();
  const [imgDisplayLoaded, setImgDisplayLoaded] = useState(Boolean);
  const [note, setNote] = useState("");
  const [driveFetching, setDriveFetching] = useState(false);
  const [uploadedImageTempUrl, setUploadedImageTempUrl] = useState([]);
  const [savePhoto, { loading: imgSaving }] = useMutation(NEW_CLIENT_PHOTO);
  const [loadPhoto, { data: loadedImg, loading: imgLoading }] = useLazyQuery(
    QUERY_CLIENT_PHOTOS,
    { fetchPolicy: "network-only" }
  );

  useEffect(() => {
    const date = changeDateCompleted.length > 0 ? changeDateCompleted : Date.now();
    const formattedDate = BAGroupTimeFormat(date, false, true, true);
    setNote(formattedDate);
  }, [changeDateCompleted]);

  // Clears state for multiple uploads
  const resetState = () => {
    const nowDate = Date.now();
    const formattedDate = BAGroupTimeFormat(nowDate, false, true, true);
    clearTempUrls();
    setFiles();
    setNote(formattedDate);
  };

  const clearTempUrls = () => {
    setUploadedImageTempUrl([]);
  };

  const handleUploadSubmit = async () => {
    if (files?.length === 0) {
      return;
    }

    setImgDisplayLoaded(false);
    await changeLoading(true);

    for await (const file of files) {
      const date = Date.now();
      const resizedImg = await resizeImage(file);

      if ((await resizedImg.length) > 0) {
        const photoData = await savePhoto({
          variables: {
            imageData: resizedImg,
            beforeOrAfter: BoaProp,
            dateEntered: imgDate || date.toString(),
            note: note,
          },
        });

        if (photoData.data) {
          changePhotoIds(photoData.data.storeImage._id);
        }
      }
    }

    changeLoading(false);
    return;
  };

  const handleFileChange = (event) => {
    clearTempUrls();
    if (event.target.files.length === 0) {
    }

    if (event.target.files) {
      setFiles([...event.target.files]);

      return;
    }
  };

  const parseDriveFileId = (url) => {
    if (!url) return null;
    // https://drive.google.com/open?id=FILE_ID
    const idParam = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (idParam) return idParam[1];
    // https://drive.google.com/file/d/FILE_ID/view
    const filePath = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (filePath) return filePath[1];
    // https://drive.google.com/uc?id=FILE_ID
    const ucParam = url.match(/\/uc\?.*id=([a-zA-Z0-9_-]+)/);
    if (ucParam) return ucParam[1];
    return null;
  };

  const handleDriveClipboard = async () => {
    try {
      const clipText = await navigator.clipboard.readText();
      const fileId = parseDriveFileId(clipText);
      if (!fileId) return;

      setDriveFetching(true);
      const res = await fetch(`/image/retrieval/${fileId}`);
      const data = await res.json();

      if (data?.image64) {
        const response = await fetch(data.image64);
        const blob = await response.blob();
        const file = new File([blob], `drive-${fileId}.jpg`, {
          type: blob.type || "image/jpeg",
        });

        clearTempUrls();
        setFiles([file]);
      }
    } catch (e) {
      console.error("Drive fetch failed:", e);
    } finally {
      setDriveFetching(false);
    }
  };

  const handleIdChange = async (event) => {
    const name = event.target.name;
    const value = event.target.value;

    clearTempUrls();

    if (name === "note") {
      setNote(value);
      return;
    }

    if (value.length >= 24) {
      setImgDisplayLoaded(true);
      await loadPhoto({ variables: { pictureIdArr: [value] } });
      changePhotoIds(value);

      return;
    }

    setImgDisplayLoaded(null);
    changePhotoIds("");
  };

  const createTemps = async () => {
    const b64toBlob = (
      b64Data,
      contentType = "image/jpeg",
      sliceSize = 512
    ) => {
      const splitData = b64Data.split(",")[1];
      const byteCharacters = atob(splitData);
      const byteArrays = [];

      for (
        let offset = 0;
        offset < byteCharacters.length;
        offset += sliceSize
      ) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      const blob = new Blob(byteArrays, { type: contentType });
      return blob;
    };

    if (files) {
      for (const file of files) {
        const newSize = await resizeImage(file);
        const blob = b64toBlob(newSize);
        const url = URL.createObjectURL(blob);
        const name = file.name;

        setUploadedImageTempUrl((uploadedImageTempUrl) => [
          ...uploadedImageTempUrl,
          { url, name },
        ]);
      }

      handleUploadSubmit();

      return;
    }
  };

  useEffect(() => {
    createTemps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  useEffect(() => {
    if (clearState) {
      resetState();
      clearStateTriggerCB();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearState]);

  return (
    <div className="uploaded-container">
      <div className="id-input">
        <BdrInput
          className="bdr-field--w-95"
          fontSize="8px"
          type="text"
          label={`Enter photo Id`}
          inputMode="text"
          name="photoId"
          onChange={handleIdChange}
        />
        <BdrInput
          className="bdr-field--w-95"
          fontSize="8px"
          type="text"
          label={`Note:`}
          inputMode="text"
          name="note"
          value={note}
          onChange={handleIdChange}
        />
      </div>
      <span style={{ opacity: 0.6, margin: "0 0 10px" }}>or</span>
      <div className="upload-buttons-row">
        <label className="custom-file-upload">
          <input
            onChange={handleFileChange}
            type="file"
            accept="image/*"
            name="clientImageUpload"
          />
          <FileUploadIcon sx={{ fontSize: "16px" }} /> Select File
        </label>
        <button
          className="custom-file-upload"
          onClick={handleDriveClipboard}
          disabled={driveFetching}
        >
          <LinkIcon sx={{ fontSize: "16px" }} />
          {driveFetching ? "Fetching..." : "Paste Drive Link"}
        </button>
      </div>
      <div className="uploaded-box relative">
        {imgDisplayLoaded ? (
          <>
            <div className={imgLoading ? "over-loader" : "none"}>
              <BdrSpinner message={"Loading..."} />
            </div>
            {loadedImg && (
              <img
                className="admin-uploaded-pic"
                src={
                  loadedImg.getImageById[0].s3ImageUrl ??
                  baImageUrl(loadedImg.getImageById[0]._id)
                }
                alt="Client asset loaded"
              />
            )}
          </>
        ) : (
          <>
            <div className={imgSaving ? "over-loader" : "none"}>
              <BdrSpinner message={"Saving..."} />
            </div>
            {uploadedImageTempUrl &&
              uploadedImageTempUrl.map((file) => {
                return (
                  <img
                    className="admin-uploaded-pic"
                    key={file.name + Date.now()}
                    src={file.url}
                    alt="Client upload"
                  />
                );
              })}
          </>
        )}
      </div>
    </div>
  );
};
export default PictureUploadAdmin;
